import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkScrollable, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';

import { HintsDisplay, ImageUrl, NewLineToHtml, SafeHTML } from '@common/pipes';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { ProductInfoModel } from './product-Info.model';
import { Rating } from '@proto/store/api/common/common';
import { Product } from '@proto/store/api/store/product';
import { EmptyPlaceholderComponent } from '@common/empty-placeholder/empty-placeholder.component';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { RatingListComponent } from '@common/rating-list/rating-list.component';
import { Constants } from '@helper/constants';

import { Subject } from 'rxjs';

export enum InfoType {
  RATING = 'rating',
  PRODUCT_DETAIL = 'detail',
  SIZE_TABLE = 'size',
  FITTING_TABLE = 'fitting',
}

interface IDialogData {
  type: InfoType;
  product: Product;
}

@Component({
  selector: 'app-product-info',
  templateUrl: './product-info.component.html',
  styleUrls: ['./product-info.component.scss'],
  imports: [
    NewLineToHtml,
    SafeHTML,
    ImageLoadingComponent,
    HintsDisplay,
    EmptyPlaceholderComponent,
    FocusRemoverDirective,
    MatDialogModule,
    RatingListComponent,
    ScrollingModule,
    ImageUrl,
  ],
  providers: [ProductInfoModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductInfoComponent implements OnInit {
  readonly loadMore = new Subject<boolean>();
  readonly data: IDialogData = inject(MAT_DIALOG_DATA);
  readonly InfoType = InfoType;
  readonly imgWidth = 453;
  readonly ratingHeight = 630;

  @ViewChild('container') container?: ElementRef;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public ratingsViewModel: ProductInfoModel,
    public scrollDispatcher: ScrollDispatcher
  ) {
    this.ratingsViewModel.itemCount.set(Constants.displayCount(ratingsViewModel.detectService.isMobileDevice()));
    this.scrollDispatcher.scrolled().subscribe((event: void | CdkScrollable) => {
      const evt = event as CdkScrollable;
      if (evt) {
        const scrollContainer = evt.getElementRef().nativeElement;
        const ele = scrollContainer.firstElementChild;
        if (ele && scrollContainer.scrollTop > ele.scrollHeight - 2 * this.ratingHeight) {
          this.loadMore.next(true);
        }
      }
    });
    this.handleLoadMore();
  }

  static open(dialog: MatDialog, type: InfoType, product: Product): MatDialogRef<ProductInfoComponent> {
    const data: IDialogData = { type, product: Product.fromPartial(product) };
    return dialog.open(ProductInfoComponent, { width: '700px', height: '800px', data });
  }

  ngOnInit(): void {
    if (!this.data.product.fittingReportImage && this.data.type === InfoType.FITTING_TABLE) {
      this.data.type = InfoType.RATING;
    }
    this.ratingsViewModel.listRatings(this.destroyRef, this.data.product.meta?.productId ?? '');
  }

  trackByFn(_: number, rating: Rating): string {
    return rating.id;
  }

  handleLoadMore(): void {
    this.loadMore.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.data.type !== InfoType.RATING) {
        return;
      }
      if (this.ratingsViewModel.itemCount() < this.ratingsViewModel.allRatings().length) {
        this.ratingsViewModel.itemCount.update(value =>
          Math.min(
            value + Constants.displayCount(this.ratingsViewModel.detectService.isMobileDevice()),
            this.ratingsViewModel.allRatings().length
          )
        );
      } else if (this.ratingsViewModel.nextPageToken()) {
        this.ratingsViewModel.listRatings(this.destroyRef, this.data.product.meta?.productId ?? '');
      }
    });
  }

  onTypeChange(type: InfoType): void {
    this.data.type = type;
    if (this.container) {
      this.container.nativeElement.scrollTop = 0;
    }
  }
}
